/**
 * UGC Gallery — Shop the Look
 *
 * Handles:
 *  - Gallery slider (CSS scroll, no JS needed)
 *  - Click thumbnail → open popup at the correct slide
 *  - Open popup from URL: ?look=1
 *  - Scroll to section before opening URL look popup
 *  - Slide prev / next
 *  - Black overlay backdrop — click outside to close
 *  - Hotspot ↔ product card cross-highlight
 *  - Variant pill selection → update ATC variant + price
 *  - Add to Cart via /cart/add.js
 *  - Show toast only after add to cart
 *  - View cart button closes popup and opens cart drawer
 *  - Theme editor: selecting a block opens its popup automatically
 */
(function () {
  'use strict';

  let modal  = document.getElementById('ugc-modal');
  let slides = document.getElementById('ugc-slides');

  const toast = document.getElementById('ugc-toast');
  const toastMsg = document.getElementById('ugc-toast-msg');
  const toastViewCart = toast?.querySelector('.ugc-toast__cta');

  if (!modal || !slides) return;

  const allSlides = () => [...slides.querySelectorAll('.ugc-slide')];

  let currentSlide = 0;
  let toastTimer = null;

  /* ── Slide navigation ──────────────────────────────────────── */
  function goTo(index) {
    const slideEls = allSlides();
    const count = slideEls.length;

    if (count === 0) return;

    index = ((index % count) + count) % count;

    slideEls.forEach((slide, i) => {
      slide.hidden = i !== index;
    });

    currentSlide = index;
  }

  /* ── Open / Close modal ────────────────────────────────────── */
  function openModal(slideIndex) {
    goTo(slideIndex);

    modal.hidden = false;
    document.body.style.overflow = 'hidden';

    modal.querySelector('.ugc-close-btn')?.focus();
  }

  function closeModal() {
    if (!modal) return;

    modal.hidden = true;
    document.body.style.overflow = '';
  }

  /* ── Open modal from URL look ──────────────────────────────── */
  function openModalFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const rawIndex = params.get('look');

    if (rawIndex === null) return false;

    const requestedIndex = parseInt(rawIndex, 10);
    if (Number.isNaN(requestedIndex) || requestedIndex < 1) return false;

    const slideEls = allSlides();
    if (!slideEls.length) return false;

    /*
      URL index is 1-based:
      ?look=1 = first slide
      ?look=2 = second slide
    */
    const targetIndex = Math.min(requestedIndex, slideEls.length) - 1;

    const trigger = document.querySelector(`[data-open-slide="${targetIndex}"]`);

    if (trigger) {
      const section =
        trigger.closest('section') ||
        trigger.closest('.shopify-section') ||
        trigger.closest('[id^="shopify-section-"]') ||
        trigger.closest('.ugc-section') ||
        trigger;

      const header =
        document.querySelector('sticky-header') ||
        document.querySelector('.shopify-section-header') ||
        document.querySelector('#shopify-section-header') ||
        document.querySelector('header');

      const headerOffset = header ? header.offsetHeight : 0;
      const sectionTop = section.getBoundingClientRect().top + window.pageYOffset - headerOffset - 20;

      window.scrollTo({
        top: sectionTop,
        behavior: 'smooth',
      });

      setTimeout(() => {
        openModal(targetIndex);
      }, 500);
    } else {
      openModal(targetIndex);
    }

    return true;
  }

  /* ── Cart drawer open helper ───────────────────────────────── */
  function openCartDrawer() {
    /*
      1. Try common custom element cart drawer methods.
      2. Try common drawer classes.
      3. Try clicking the cart icon.
      4. Fallback to /cart.
    */

    const cartDrawer =
      document.querySelector('cart-drawer') ||
      document.querySelector('#CartDrawer') ||
      document.querySelector('.cart-drawer') ||
      document.querySelector('[data-cart-drawer]');

    if (cartDrawer) {
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }

      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show();
        return;
      }

      cartDrawer.classList.add('active', 'animate', 'is-open', 'drawer--is-open');
      cartDrawer.removeAttribute('hidden');
      cartDrawer.setAttribute('open', '');

      document.body.classList.add('overflow-hidden');
      document.documentElement.classList.add('overflow-hidden');

      return;
    }

    const cartNotification = document.querySelector('cart-notification');

    if (cartNotification) {
      if (typeof cartNotification.open === 'function') {
        cartNotification.open();
        return;
      }

      cartNotification.classList.add('active', 'animate', 'is-open');
      cartNotification.removeAttribute('hidden');
      cartNotification.setAttribute('open', '');

      return;
    }

    document.dispatchEvent(new CustomEvent('cart:open'));
    document.dispatchEvent(new CustomEvent('cart-drawer:open'));

    const cartTrigger =
      document.querySelector('[data-cart-drawer-open]') ||
      document.querySelector('[data-cart-trigger]') ||
      document.querySelector('[aria-controls="CartDrawer"]') ||
      document.querySelector('#cart-icon-bubble') ||
      document.querySelector('a[href="/cart"]') ||
      document.querySelector('a[href*="/cart"]');

    if (cartTrigger) {
      cartTrigger.click();
      return;
    }

    window.location.href = '/cart';
  }

  /* ── View cart from toast ──────────────────────────────────── */
  if (toastViewCart) {
    toastViewCart.addEventListener('click', (event) => {
      event.preventDefault();

      closeModal();
      hideToast();
      openCartDrawer();
    });
  }

  /* ── Modal clicks ──────────────────────────────────────────── */
  modal.addEventListener('click', (event) => {
    if (event.target.closest('[data-close]')) {
      closeModal();
      return;
    }

    if (event.target.closest('.ugc-nav-btn--prev')) {
      goTo(currentSlide - 1);
      return;
    }

    if (event.target.closest('.ugc-nav-btn--next')) {
      goTo(currentSlide + 1);
      return;
    }
  });

  /* ── Keyboard navigation ───────────────────────────────────── */
  document.addEventListener('keydown', (event) => {
    if (modal.hidden) return;

    if (event.key === 'ArrowLeft') {
      goTo(currentSlide - 1);
    }

    if (event.key === 'ArrowRight') {
      goTo(currentSlide + 1);
    }

    if (event.key === 'Escape') {
      closeModal();
    }
  });

  /* ── Gallery grid clicks ───────────────────────────────────── */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-open-slide]');
    if (!btn) return;

    openModal(Number(btn.dataset.openSlide));
  });

  /* ── Hotspot ↔ Product highlight ──────────────────────────── */
  slides.addEventListener('mouseover', (event) => {
    const hs = event.target.closest('.ugc-hotspot');
    const prod = event.target.closest('.ugc-product');

    if (hs) {
      activateHotspot(hs.closest('.ugc-slide'), Number(hs.dataset.hs));
    } else if (prod) {
      activateHotspot(prod.closest('.ugc-slide'), Number(prod.dataset.pnum));
      prod.classList.add('is-active');
    }
  });

  slides.addEventListener('mouseout', (event) => {
    const hs = event.target.closest('.ugc-hotspot');
    const prod = event.target.closest('.ugc-product');
    const relTarget = event.relatedTarget;

    if (hs && !hs.contains(relTarget)) {
      deactivateHotspot(hs.closest('.ugc-slide'), Number(hs.dataset.hs));
    }

    if (prod && !prod.contains(relTarget)) {
      deactivateHotspot(prod.closest('.ugc-slide'), Number(prod.dataset.pnum));
      prod.classList.remove('is-active');
    }
  });

  function activateHotspot(slide, num) {
    slide?.querySelector(`.ugc-hotspot[data-hs="${num}"]`)?.classList.add('is-active');
  }

  function deactivateHotspot(slide, num) {
    slide?.querySelector(`.ugc-hotspot[data-hs="${num}"]`)?.classList.remove('is-active');
  }

  /* ── Variant pill selection ────────────────────────────────── */
  slides.addEventListener('click', (event) => {
    const pill = event.target.closest('.ugc-vpill');

    if (!pill || pill.disabled) return;

    const optRow = pill.closest('.ugc-product__opt-row');
    if (!optRow) return;

    optRow.querySelectorAll('.ugc-vpill').forEach((p) => {
      p.classList.remove('is-on');
    });

    pill.classList.add('is-on');

    const card = pill.closest('.ugc-product');
    const variant = resolveVariant(card);

    if (variant) {
      updateCard(card, variant);
      updateVariantPills(card);
    }
  });

  function resolveVariant(card) {
    let variants;

    try {
      variants = JSON.parse(card.dataset.variants || '[]');
    } catch (error) {
      console.warn('[UGC] variants JSON parse failed:', error, card.dataset.variants);
      return null;
    }

    if (!variants.length) return null;

    const selected = {};

    card.querySelectorAll('.ugc-vpill.is-on').forEach((pill) => {
      selected[`o${pill.dataset.opt}`] = pill.dataset.val;
    });

    return (
      variants.find((variant) =>
        Object.entries(selected).every(([key, val]) => String(variant[key]) === String(val))
      ) ||
      variants.find((variant) => variant.avail) ||
      variants[0]
    );
  }

  function updateCard(card, variant) {
    const priceEl = card.querySelector('[data-price]');

    if (priceEl && variant.price != null) {
      priceEl.textContent = formatMoney(variant.price);
    }

    if (variant.img) {
      const imgEl = card.querySelector('.ugc-product__img-wrap img');

      if (imgEl) {
        imgEl.src = variant.img;
      }
    }

    const atcBtn = card.querySelector('.ugc-atc');

    if (atcBtn) {
      atcBtn.setAttribute('data-vid', String(variant.id));
      atcBtn.disabled = !variant.avail;

      const textEl = atcBtn.querySelector('.ugc-atc__text');

      if (textEl) {
        textEl.textContent = variant.avail ? 'Add to Cart' : 'Sold Out';
      }
    }
  }

  function updateVariantPills(card) {
    let variants;

    try {
      variants = JSON.parse(card.dataset.variants || '[]');
    } catch {
      return;
    }

    if (!variants.length) return;

    const selected = {};

    card.querySelectorAll('.ugc-vpill.is-on').forEach((pill) => {
      selected[`o${pill.dataset.opt}`] = pill.dataset.val;
    });

    card.querySelectorAll('.ugc-vpill').forEach((pill) => {
      const opt = pill.dataset.opt;
      const val = pill.dataset.val;

      const isAvailable = variants.some((variant) => {
        if (!variant.avail) return false;
        if (String(variant[`o${opt}`]) !== String(val)) return false;

        return Object.entries(selected).every(([key, selectedVal]) => {
          if (key === `o${opt}`) return true;
          return String(variant[key]) === String(selectedVal);
        });
      });

      pill.classList.toggle('is-out', !isAvailable);
      pill.disabled = !isAvailable;
    });
  }

  /* ── Add to Cart ───────────────────────────────────────────── */
  slides.addEventListener('click', async (event) => {
    const btn = event.target.closest('.ugc-atc');

    if (!btn || btn.disabled) return;

    const variantId = btn.getAttribute('data-vid');
    if (!variantId) return;

    btn.disabled = true;
    btn.classList.add('is-busy');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
        }),
      });

      if (!response.ok) {
        throw new Error('cart_error');
      }

      const item = await response.json();

      btn.classList.remove('is-busy');
      btn.classList.add('is-done');

      const textEl = btn.querySelector('.ugc-atc__text');

      if (textEl) {
        textEl.textContent = 'Added!';
      }

      /*
        Important:
        Do NOT open cart drawer here.
        Do NOT close modal here.
        Only show toast.
      */
      showToast(`${item.product_title || item.title || 'Product'} added to cart`);
      refreshCartCount();

      document.dispatchEvent(
        new CustomEvent('ugc:cart-added', {
          detail: {
            item,
            variantId,
          },
        })
      );

      setTimeout(() => {
        btn.classList.remove('is-done');

        if (textEl) {
          textEl.textContent = 'Add to Cart';
        }

        btn.disabled = false;
      }, 2200);
    } catch (error) {
      btn.classList.remove('is-busy');
      btn.disabled = false;

      showToast('Could not add to cart. Please try again.');
    }
  });

  /* ── Cart count refresh ────────────────────────────────────── */
  function refreshCartCount() {
    fetch('/cart.js', {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.json())
      .then((cart) => {
        document
          .querySelectorAll(
            '[data-cart-count], .cart-count, .header__cart-count, [data-header-cart-count], .js-cart-count, .cart-count-bubble span'
          )
          .forEach((el) => {
            el.textContent = cart.item_count;
          });

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: cart,
          })
        );
      })
      .catch(() => {});
  }

  /* ── Toast ─────────────────────────────────────────────────── */
  function showToast(message) {
    if (!toast) return;

    if (toastMsg) {
      toastMsg.textContent = message;
    }

    toast.hidden = false;

    requestAnimationFrame(() => {
      toast.classList.add('is-on');
    });

    clearTimeout(toastTimer);

    toastTimer = setTimeout(() => {
      hideToast();
    }, 4500);
  }

  function hideToast() {
    if (!toast) return;

    clearTimeout(toastTimer);

    toast.classList.remove('is-on');

    setTimeout(() => {
      toast.hidden = true;
    }, 280);
  }

  /* ── Money format ──────────────────────────────────────────── */
  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';

    try {
      return new Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /* ── Theme editor integration ─────────────────────────────── */
  document.addEventListener('shopify:block:select', (event) => {
    modal = document.getElementById('ugc-modal');
    slides = document.getElementById('ugc-slides');

    if (!modal || !slides) return;

    const slide = slides.querySelector(`[data-block-id="${event.detail.blockId}"]`);
    if (!slide) return;

    openModal(Number(slide.dataset.slide));
  });

  document.addEventListener('shopify:block:deselect', () => {
    closeModal();
  });

  document.addEventListener('shopify:section:deselect', () => {
    closeModal();
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (event.detail.sectionId !== modal?.dataset.sectionId) return;

    modal = document.getElementById('ugc-modal');
    slides = document.getElementById('ugc-slides');

    closeModal();

    if (window.location.search.includes('look=')) {
      openModalFromUrl();
    }
  });

  /* ── Init ──────────────────────────────────────────────────── */
  if (!openModalFromUrl()) {
    goTo(0);
  }
})();